import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";

import { Panel, PanelBody, PanelHeader } from "../../components/Panel";
import { Box } from "../../components/Box";
import { Text } from "../../components/Text";
import Loader from "../../components/Loader";
import { BlocksByDayInsight, TagTime } from "@aika/types";
import { GraphStream, LineGraph } from "./LineGraph";
import { addDays, differenceInDays, format, min } from "date-fns";
import { groupBy, sortBy, sum, range as _range } from "lodash";
import { FaSlidersH } from "react-icons/fa";
import { Flex } from "components/Flex";
import { IconButton } from "components/IconButton";
import Modal from "components/Modal";
import { MultiSelect } from "components/MultiSelect";
import Button from "components/Button";
import { ControlLabel } from "components/ControlLabel";
import { DateRange, dateRangeValue } from "components/DateRangeControl";

type ConfigModalProps = {
  tags: TagTime[];
  activeTags: string[];
  onClose: () => void;
  onSave: (tags: string[]) => void;
};

function ConfigModal({ tags, activeTags, onClose, onSave }: ConfigModalProps) {
  const [active, setActive] = useState(activeTags);
  const tagOpts = sortBy(tags, "name").map((x) => ({
    value: x.id,
    label: x.name,
  }));

  const setTags = (vals: string[]) => {
    if (vals.length > 7) {
      window.alert("Maximum of 7 tags");
    } else {
      setActive(vals);
    }
  };

  return (
    <Modal onClose={onClose} width={400}>
      <Box mb={2}>
        <ControlLabel>Tags to Include</ControlLabel>
        <MultiSelect options={tagOpts} value={active} onChange={setTags} />
      </Box>
      <Flex justifyContent="flex-end">
        <Button onClick={() => onSave(active)}>Save</Button>
      </Flex>
    </Modal>
  );
}

const TAGS_KEY = "aika_blocks_by_day-tags";

export function BlocksByDayWidget({ range }: { range: DateRange }) {
  const [tags, setTags] = useState<TagTime[]>([]);
  const [data, setData] = useState<BlocksByDayInsight>(null!);
  const [showingConfig, setShowingConfig] = useState<boolean>(false);
  const [activeTags, setActiveTags] = useState<string[] | null>(
    JSON.parse(window.localStorage.getItem(TAGS_KEY) ?? "null")
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.localStorage.setItem(TAGS_KEY, JSON.stringify(activeTags));
  }, [activeTags]);

  useEffect(() => {
    axios.get<TagTime[]>("/api/reports/time-by-tag").then(({ data }) => {
      const lsTags = JSON.parse(
        window.localStorage.getItem(TAGS_KEY) ?? "null"
      );
      if (lsTags == null) {
        const topThree = sortBy(data, (x) => x.past_time)
          .reverse()
          .slice(0, 3)
          .map((x) => x.id);
        setActiveTags(topThree);
      }
      setActiveTags((old) => old.filter((o) => data.some((x) => x.id === o)));
      setTags(data);
    });
  }, []);

  const getData = useCallback((tagIds: string[], range: DateRange) => {
    setLoading(true);
    const dates = dateRangeValue(range);
    let url = `/api/insights/blocks-per-day?start=${format(
      dates[0],
      "yyyy-MM-dd"
    )}&end=${format(dates[1], "yyyy-MM-dd")}`;

    if (tagIds.length > 0) {
      url += `&tags=${tagIds.join(",")}`;
    }

    axios.get<BlocksByDayInsight>(url).then(({ data }) => {
      setData(data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (activeTags != null) {
      getData(activeTags, range);
    }
  }, [activeTags, range, getData]);

  const streams = useMemo<GraphStream>(() => {
    if (data == null) {
      return [];
    }

    const dates = dateRangeValue(range);
    const dataDates = data.total.map((x) => new Date(x.day));
    const minDate = range === "alltime" ? min(dataDates) : dates[0];
    const maxDate = dates[1];

    const grouped = groupBy(data.total, "day");
    const tagGroups = activeTags.map((x) => groupBy(data.tags[x], "day"));
    const len = differenceInDays(maxDate, minDate);

    return _range(0, len).map((x) => {
      const date = addDays(minDate, x);
      const groupName = format(date, "yyyy-MM-dd");
      const items = grouped[groupName] ?? [];
      const tagItems = tagGroups.map((x) => x[groupName] ?? []);
      return [
        format(date, "dd/MM/yy"),
        [
          sum(items.map((x) => x.totalTime)) ?? 0,
          ...tagItems.map((x) => sum(x.map((x) => x.totalTime)) ?? 0),
        ],
      ];
    });
  }, [data, range, activeTags]);

  console.log(streams);

  const labels =
    activeTags == null || tags.length === 0
      ? ["Total"]
      : [
          "Total",
          ...activeTags.map((x) => "#" + tags.find((t) => t.id === x)?.name),
        ];

  const onConfigSave = (tags: string[]) => {
    setActiveTags(tags);
    setShowingConfig(false);
  };

  return (
    <>
      {showingConfig && (
        <ConfigModal
          tags={tags}
          activeTags={activeTags}
          onClose={() => setShowingConfig(false)}
          onSave={onConfigSave}
        />
      )}
      <Panel p={0}>
        <PanelHeader>
          <Flex justifyContent="space-between" alignItems="center">
            <Text fontSize={[3, 4]} fontWeight={600}>
              Time Scheduled by Date
            </Text>
            <IconButton
              icon={FaSlidersH}
              onClick={() => setShowingConfig(true)}
            />
          </Flex>
        </PanelHeader>
        <PanelBody>
          <Flex
            alignItems="center"
            justifyContent="center"
            height="100%"
            flex="1"
          >
            {loading && <Loader overlay />}
            <LineGraph
              data={loading ? [] : streams}
              xAxis="Date (dd/mm/yy)"
              yAxis="Time (mins)"
              streamLabels={labels}
              maxXLabels={16}
            />
          </Flex>
        </PanelBody>
      </Panel>
    </>
  );
}
